import React, {useEffect, useState} from "react";
import {Box, Button, CircularProgress, TextField, Typography} from "@mui/material";

const memberLimitFallback = 3;

const errorMessage = (error) => {
    const status = error?.response?.status;
    if (status === 403) return "Workspace members can only be managed by the owner of a lifetime workspace.";
    if (status === 404) return "This member or invitation is no longer available.";
    if (status === 409) return "The workspace is full, or this invitation has already been accepted.";
    return "Something went wrong. Please try again.";
};

const avatarText = (name, email) => String(name || email || "M").trim().charAt(0).toUpperCase();

const primaryActionButtonStyle = {
    height: "38px",
    minWidth: "118px",
    borderRadius: "10px",
    textTransform: "none",
    fontFamily: "Inter",
    fontWeight: 600,
    color: "var(--app-bg-surface)",
    backgroundColor: "var(--app-text-primary)",
    "&:hover": {
        backgroundColor: "var(--app-text-primary)",
        opacity: 0.9,
    },
};

const rowActionButtonStyle = {
    minWidth: "auto",
    borderRadius: "8px",
    textTransform: "none",
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 600,
    color: "var(--app-text-primary)",
    borderColor: "var(--app-border-muted)",
    "&:hover": {
        borderColor: "var(--app-text-primary)",
        backgroundColor: "var(--app-bg-subtle)",
    },
};

const PersonRow = ({name, email, role, pending = false, actionLabel, actionDisabled, onAction}) => (
    <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "10px 0",
        borderBottom: "1px solid var(--app-border-subtle)",
        "&:last-child": {borderBottom: "none"},
    }}>
        <Box sx={{
            width: "34px",
            height: "34px",
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "var(--app-bg-subtle)",
            color: "var(--app-text-primary)",
            fontFamily: "Inter",
            fontSize: "13px",
            fontWeight: 650,
        }}>
            {avatarText(name, email)}
        </Box>
        <Box sx={{flex: 1, minWidth: 0}}>
            <Typography noWrap sx={{
                fontFamily: "Inter",
                fontSize: "14px",
                fontWeight: 600,
                color: "var(--app-text-primary)",
            }}>
                {name || email || "Member"}
            </Typography>
            {name && email ? (
                <Typography noWrap sx={{fontFamily: "Inter", fontSize: "12px", color: "var(--app-text-muted)"}}>
                    {email}
                </Typography>
            ) : null}
        </Box>
        <Typography sx={{fontFamily: "Inter", fontSize: "12px", color: "var(--app-text-muted)"}}>
            {pending ? "Pending editor" : role === "owner" ? "Owner" : "Editor"}
        </Typography>
        {onAction ? (
            <Button
                variant="outlined"
                size="small"
                disabled={actionDisabled}
                onClick={onAction}
                sx={rowActionButtonStyle}
            >
                {actionLabel}
            </Button>
        ) : null}
    </Box>
);

const WorkspaceMembersSettings = ({workspace, authContext, fetchContext}) => {
    const workspaceId = workspace?.workspaceId;
    const getFreshToken = authContext.getFreshToken;
    const authAxios = fetchContext.authAxios;
    const [members, setMembers] = useState([]);
    const [pendingInvites, setPendingInvites] = useState([]);
    const [memberLimit, setMemberLimit] = useState(memberLimitFallback);
    const [inviteEmail, setInviteEmail] = useState("");
    const [loading, setLoading] = useState(true);
    const [inviting, setInviting] = useState(false);
    const [removingKey, setRemovingKey] = useState("");
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        let active = true;
        if (!workspaceId) return undefined;

        setLoading(true);
        setError("");
        setMembers([]);
        setPendingInvites([]);
        setMemberLimit(memberLimitFallback);
        (async () => {
            try {
                const token = await getFreshToken?.();
                if (!token) throw new Error("Missing authentication token");
                const {data} = await authAxios.get(
                    `/workspaces/${encodeURIComponent(workspaceId)}/members`,
                    {headers: {Authorization: `Bearer ${token}`}}
                );
                if (!active) return;
                setMembers(Array.isArray(data?.members) ? data.members : []);
                setPendingInvites(Array.isArray(data?.pendingInvites) ? data.pendingInvites : []);
                setMemberLimit(Number(data?.memberLimit) || memberLimitFallback);
            } catch (loadError) {
                if (active) setError(errorMessage(loadError));
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [workspaceId, refreshKey, getFreshToken, authAxios]);

    const handleInvite = async () => {
        const email = inviteEmail.trim();
        if (!email || !email.includes("@") || inviting) {
            if (!email || !email.includes("@")) setError("Enter a valid email address.");
            return;
        }

        setInviting(true);
        setError("");
        setNotice("");
        try {
            const token = await getFreshToken?.();
            if (!token) throw new Error("Missing authentication token");
            const {data} = await authAxios.post(
                `/workspaces/${encodeURIComponent(workspaceId)}/invites`,
                {email, role: "editor"},
                {headers: {Authorization: `Bearer ${token}`}}
            );
            setInviteEmail("");
            setNotice(`Invitation sent to ${data?.emailLower || email}.`);
            setRefreshKey((value) => value + 1);
        } catch (inviteError) {
            setError(errorMessage(inviteError));
        } finally {
            setInviting(false);
        }
    };

    const handleRemove = async ({path, key, noticeText, updateRows}) => {
        if (!workspaceId || removingKey) return;
        setRemovingKey(key);
        setError("");
        setNotice("");
        try {
            const token = await getFreshToken?.();
            if (!token) throw new Error("Missing authentication token");
            await authAxios.delete(path, {headers: {Authorization: `Bearer ${token}`}});
            updateRows();
            setNotice(noticeText);
        } catch (removeError) {
            setError(errorMessage(removeError));
        } finally {
            setRemovingKey("");
        }
    };

    const cancelInvite = (email) => handleRemove({
        path: `/workspaces/${encodeURIComponent(workspaceId)}/invites/${encodeURIComponent(email)}`,
        key: `invite:${email}`,
        noticeText: `Invitation to ${email} canceled.`,
        updateRows: () => setPendingInvites((current) => current.filter((invite) => invite.emailLower !== email)),
    });

    const removeMember = (member) => handleRemove({
        path: `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(member.userId)}`,
        key: `member:${member.userId}`,
        noticeText: `${member.displayName || member.email || "Member"} removed.`,
        updateRows: () => setMembers((current) => current.filter((item) => item.userId !== member.userId)),
    });

    const atMemberLimit = members.length >= memberLimit;

    return (
        <>
            <Box sx={{display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "20px"}}>
                <Box>
                    <Typography sx={{
                        fontFamily: "Inter",
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                    }}>
                        Members of {workspace?.name || "Workspace"}
                    </Typography>
                    <Typography sx={{fontFamily: "Inter", fontSize: "13px", color: "var(--app-text-muted)", mt: "3px"}}>
                        {members.length} of {memberLimit} member seats used
                    </Typography>
                </Box>
            </Box>

            <Box sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                padding: "14px",
                border: "1px solid var(--app-border-subtle)",
                borderRadius: "12px",
                backgroundColor: "var(--app-bg-surface)",
            }}>
                <TextField
                    value={inviteEmail}
                    onChange={(event) => setInviteEmail(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") handleInvite();
                    }}
                    type="email"
                    size="small"
                    fullWidth
                    disabled={loading || inviting || atMemberLimit}
                    placeholder="Email address"
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            height: "38px",
                            fontFamily: "Inter",
                            fontSize: "14px",
                            borderRadius: "10px",
                        },
                    }}
                />
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleInvite}
                    disabled={loading || inviting || atMemberLimit}
                    sx={primaryActionButtonStyle}
                >
                    {inviting ? "Sending…" : "Invite editor"}
                </Button>
            </Box>

            {atMemberLimit ? (
                <Typography sx={{fontFamily: "Inter", fontSize: "12px", color: "var(--app-text-muted)"}}>
                    This workspace has reached its {memberLimit}-member limit.
                </Typography>
            ) : null}
            {error ? (
                <Typography sx={{fontFamily: "Inter", fontSize: "12px", color: "#C63636"}}>{error}</Typography>
            ) : null}
            {notice ? (
                <Typography sx={{fontFamily: "Inter", fontSize: "12px", color: "#2E7D32"}}>{notice}</Typography>
            ) : null}

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", padding: "28px"}}>
                    <CircularProgress size={20}/>
                </Box>
            ) : (
                <Box>
                    {members.map((member) => (
                        <PersonRow
                            key={member.userId}
                            name={member.displayName}
                            email={member.email}
                            role={member.role}
                            actionLabel={removingKey === `member:${member.userId}` ? "Removing…" : "Remove"}
                            actionDisabled={Boolean(removingKey)}
                            onAction={member.role === "owner" ? null : () => removeMember(member)}
                        />
                    ))}
                    {pendingInvites.length > 0 ? (
                        <>
                            <Typography sx={{
                                fontFamily: "Inter",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "var(--app-text-muted)",
                                marginTop: "22px",
                                marginBottom: "4px",
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                            }}>
                                Pending invitations
                            </Typography>
                            {pendingInvites.map((invite) => (
                                <PersonRow
                                    key={invite.emailLower}
                                    email={invite.emailLower}
                                    role={invite.role}
                                    pending
                                    actionLabel={removingKey === `invite:${invite.emailLower}` ? "Canceling…" : "Cancel"}
                                    actionDisabled={Boolean(removingKey)}
                                    onAction={() => cancelInvite(invite.emailLower)}
                                />
                            ))}
                        </>
                    ) : null}
                </Box>
            )}
        </>
    );
};

export default WorkspaceMembersSettings;
